import { useLazyQuery } from '@apollo/client';
import { DOWNLOAD_FILE } from '@gql/file';
import { useCallback } from 'react';

interface FileData {
    _id?: string;
    name: string;
    url?: string;
}

interface UseDownloadFileOptions {
    onSuccess?: (url: string) => void;
    onError?: (error: Error) => void;
}

export const useDownloadFile = (options?: UseDownloadFileOptions) => {
    const [downloadFile, { loading, error }] = useLazyQuery(DOWNLOAD_FILE, {
        onCompleted: data => {
            if (data?.downloadFile?.url) {
                options?.onSuccess?.(data.downloadFile.url);
            }
        },
        onError: err => {
            options?.onError?.(err);
        },
    });

    const download = useCallback(
        (file: FileData) => {
            if (!file._id) {
                console.warn('File ID is required for download');
                return;
            }

            // TODO: remove this when the migration from cloudinary is done
            if (file.url?.includes('cloudinary')) {
                options?.onSuccess?.(file.url);
                return;
            }

            downloadFile({ variables: { _id: file._id } });
        },
        [downloadFile, options]
    );

    const downloadFromUrl = useCallback((url: string, filename: string) => {
        fetch(url)
            .then(response => response.blob())
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename || 'downloaded-file';
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            })
            .catch(err => {
                console.error('Error downloading file:', err);
                options?.onError?.(err);
            });
    }, [options]);

    return {
        download,
        downloadFromUrl,
        loading,
        error,
    };
};
